import {
	BackdropCircularProgress,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	PAYROLL_LIST_SCREEN_SLUG,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { IResponse, IServerError } from "../../models/common";
import React, { useEffect, useState } from "react";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { ClientService } from "../../services";
import { EmployeeService } from "../../services/EmployeeService";
import { GeneralSettingsService } from "../../services/GeneralSettingsService";
import { IAppState } from "../../store/reducers";
import { PayrollService } from "../../services/PayrollService";
import { PayrollTimeSheetDataGrid } from "./PayrollTimeSheetDataGrid";
import { PayrollTimeSheetDataGridTimeInOut } from "./PayrollTimeSheetDataGridTimeInOut";
import { PayrollTimeSheetHeaderPanel } from "./Panels/PayrollTimeSheetHeaderPanel";
import { PayrollTimeSheetSetupToolbar } from "./PayrollTimeSheetSetupToolbar";
import { isEqual } from "lodash";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { useNavigateToLogout } from "../../common/Common";

export interface IPayroll { }
const PAYROLL_TIMESHEET_LIST_QUERY_KEY = "payroll-timesheet-list";
const PAY_FREQUENCY_DDL_LIST_QUERY_KEY = "pay-frequency-ddl";
const PAYROLL_PERIODS_DDL_LIST_QUERY_KEY = "payroll-periods-ddl";
const ACTIVE_EMPLOYEES_BY_PERIODS_DDL_LIST_QUERY_KEY = "payroll-active-employee-periods-ddl";
const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: [PAY_FREQUENCY_DDL_LIST_QUERY_KEY, clientId, taxYear],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayrollPeriodsDDL = (
	clientId: string, payPeriodId: string | null, taxYear: number
) =>
	useQuery({
		queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, payPeriodId, taxYear],
		queryFn: (data) => {
			return PayrollService.getPayrollPeriodsDDL(clientId, payPeriodId, taxYear);
		},
		refetchOnWindowFocus: false,
	});
const useGetActiveEmployeesByPeriodsDDL = (
	clientId: string, payPeriodId: string | null, payPeriodEndDate: string | null
) =>
	useQuery({
		queryKey: [ACTIVE_EMPLOYEES_BY_PERIODS_DDL_LIST_QUERY_KEY, clientId, payPeriodId, payPeriodEndDate],
		queryFn: (data) => {
			return PayrollService.getActiveEmployeesByPeriodsDDL(clientId, payPeriodId, payPeriodEndDate);
		},
		refetchOnWindowFocus: false,
	});
const useGetPayrollTimeSheetTableData = (
	clientId: string,
	payPeriodFrequencyId: string | null,
	payPeriodStarDate: string | null,
	payPeriodEndDate: string | null,
	selectedEmployeeId: string | null,
	workFormat: number,
	inputHours: number,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			PAYROLL_TIMESHEET_LIST_QUERY_KEY,
			clientId,
			payPeriodFrequencyId,
			payPeriodStarDate,
			payPeriodEndDate,
			selectedEmployeeId,
			workFormat,
			inputHours,
		],
		queryFn: (data) => {
			return PayrollService.getPayrollTimeSheet(
				clientId,
				payPeriodFrequencyId,
				payPeriodStarDate,
				payPeriodEndDate,
				selectedEmployeeId,
				workFormat,
				inputHours,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const CLIENT_INFO_DATA_QUERY_KEY = "client-info-data-record";
const useGetClientInformationById = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [CLIENT_INFO_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: (data) => {
			return ClientService.getSimpleClientById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});


const GENERAL_SETTING_INFO_DATA_QUERY_KEY = "general-settings-info-data-record";
const useGetGeneralSettingsData = (
	clientId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [GENERAL_SETTING_INFO_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: () => {
			return GeneralSettingsService.getByClientId(clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});
export const PayrollTimeSheetSetup: React.FC<IPayroll> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	// const companyInfo = useSelector((state: IAppState) => state.dashboardApisData.client_detail);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const storedInvoiceNoId = useSelector((state: IAppState) => state.invoice.id);
	const navigateToLogout = useNavigateToLogout();

	const screenPermissions = useSelector((state: IAppState) => state.drawerMenu.screenPermissions);
	const payrollScreenPermissions = screenPermissions.find((x: any) => x.url === '/payroll')
	// const general_settings = useSelector((state: IAppState) => state.dashboardApisData.general_settings)

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [loadingData, setIsLoading] = useState(false);
	const [selectedPayPeriodId, setSelectedPayPeriodId] = useState<null | string>(null);
	const [selectedpayPeriodIndex, setSelectedPayPeriodIndex] = useState<null | number>(null);
	const [payPeriodStarDate, setPayPeriodStarDate] = useState<null | string>(null);
	const [payPeriodEndDate, setPayPeriodEndDate] = useState<null | string>(null);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);
	const [payrollTimeSheetTableData, setPayrollTimeSheetTableData] =
		useState<any>([]);
	const [isPayrollProcessed, setIsPayrollProcessed] = useState(false);
	const [clearAllSelectedItemsForPayrollTable, setClearAllSelectedItemsForPayrollTable] = useState("fuck");
	const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
	const [workFormat, setWorkFormat] = useState(100);
	const [inputHours, setInputHours] = useState(100);

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const { isPending: isPendingClientInformationById, data: companyInfo } = useGetClientInformationById(
		clientId,
		navigateToLogout
	);

	const { isPending: isPendingGeneralSettingsInformationById, data: general_settings } = useGetGeneralSettingsData(
		clientId,
		navigateToLogout
	);
	const { isPending: isPendingPayFrequencyDDL, data: payFrequencyDDLItems } =
		useGetPayFrequencyDDL(clientId, taxYear);

	useEffect(() => {
		if (payFrequencyDDLItems && payFrequencyDDLItems.length === 1) {
			setSelectedPayPeriodId(payFrequencyDDLItems[0].value)
			let path = `/payroll/timesheet/${payFrequencyDDLItems[0].value}`
			navigate(path)
		}
	}, [payFrequencyDDLItems])

	const { id } = useParams();
	const { isPending, data } = useGetPayrollTimeSheetTableData(
		clientId,
		selectedPayPeriodId,
		payPeriodStarDate,
		payPeriodEndDate,
		selectedEmployeeId,
		workFormat,
		inputHours,
		navigateToLogout
	);
	const payrollTimeSheetData: any | null = data;
	console.log('payrollTimeSheetData', payrollTimeSheetData)

	const {
		isPending: isPendingPayrollPeriodsDDL,
		data: payrollPeriodsDDL,
	} = useGetPayrollPeriodsDDL(clientId, selectedPayPeriodId, taxYear);
	const {
		isPending: isPendingActiveEmployeesByPeriodsDDL,
		data: activeEmployeesByPeriodsDDL,
	} = useGetActiveEmployeesByPeriodsDDL(clientId, selectedPayPeriodId, payPeriodEndDate);

	useEffect(() => {
		if (payrollPeriodsDDL && payrollPeriodsDDL.length > 0 && !isPayrollProcessed) {
			let lastItem = payrollPeriodsDDL.at(-1)
			// if(lastItem.process){

			// }
			const selectedPayrollPeriodIndex = payrollPeriodsDDL.findIndex((item: any) => item.value === lastItem.value);

			formMethods.setValue('payrollPeriodsDDL', lastItem.endDate)
			formMethods.setValue('payFrequencyDDLItems', lastItem.payScheduleId)
			setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
			setPayPeriodStarDate(lastItem.startDate)
			setPayPeriodEndDate(lastItem.endDate)
		}
	}, [payrollPeriodsDDL, isPayrollProcessed])


	const { isPending: isSubmitPayrollTimeSheet, mutate: submitPayrollTimeSheet } = useMutation({
		mutationFn: PayrollService.createPayrollTimeSheet,
		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {
			if (res && res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					}),
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_TIMESHEET_LIST_QUERY_KEY],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isSaveAndCalculatePayrollTimeSheet, mutate: saveAndCalculatePayrollTimeSheet } = useMutation({
		mutationFn: PayrollService.saveAndCalculatePayrollTimeSheet,
		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {
			if (res && res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					}),
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_TIMESHEET_LIST_QUERY_KEY],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});


	const { isPending: isDeletePayrollTimeSheet, mutate: deletePayrollTimeSheet } = useMutation({
		mutationFn: PayrollService.deletePayrollTimeSheetData,
		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {
			if (res && res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					}),
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_TIMESHEET_LIST_QUERY_KEY],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const expandedLabels = ["Payroll"];
	const expandedPayrollOrSetupItem = "Time Sheet";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Payroll" &&
				item.label === expandedPayrollOrSetupItem
			) {
				isExpanded = true;
			}

			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedPayrollOrSetupItem && "/payroll/timesheet",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: PAYROLL_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);


	const onSubmit = (values: any) => {

	};

	useEffect(() => {
		if (id && !selectedPayPeriodId) {
			setSelectedPayPeriodId(id)
			formMethods.setValue('payFrequencyDDLItems', id)
		}
	}, [id])
	const onFrequencyChangeHandler = async (selectedItem: any) => {
		setSelectedPayPeriodId(selectedItem.value)
		let path = `/payroll/timesheet/${selectedItem.value}`
		navigate(path)
	};
	const onPayrollPeriodsDDLChangeHandler = async (selectedItem: any) => {
		const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");

		if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
			const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
			const selectedPayrollPeriodIndex = payrollPeriodsDDL.findIndex((item: any) => item.value === selectedPayrollPeriod.value);

			setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
			setPayPeriodStarDate(selectedPayrollPeriod.startDate)
			setPayPeriodEndDate(selectedPayrollPeriod.endDate)
		}
	};

	const onEmployeeSelectionChangeHandler = (selectedItem: any) => {
		// const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");

		// if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
		// 	const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
		// 	const selectedPayrollPeriodIndex = payrollPeriodsDDL.findIndex((item: any) => item.value === selectedPayrollPeriod.value);

		// 	setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
		// 	setPayPeriodStarDate(selectedPayrollPeriod.startDate)
		// 	setPayPeriodEndDate(selectedPayrollPeriod.endDate)
		// }
		// alert(selectedItem.label)
		setSelectedEmployeeId(selectedItem.value)
	};
	const onWorkFormatSelectionChangeHandler = (selectedItem: any) => {
		// const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");

		// if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
		// 	const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
		// 	const selectedPayrollPeriodIndex = payrollPeriodsDDL.findIndex((item: any) => item.value === selectedPayrollPeriod.value);

		// 	setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
		// 	setPayPeriodStarDate(selectedPayrollPeriod.startDate)
		// 	setPayPeriodEndDate(selectedPayrollPeriod.endDate)
		// }
		setWorkFormat(selectedItem.value)
	};
	const onInputHoursChangeHandler = (selectedItem: any) => {
		// alert(selectedItem.label)
		if (payrollTimeSheetData && payrollTimeSheetData.items && payrollTimeSheetData.items.length) {
			console.log('payrollTimeSheetData.items', payrollTimeSheetData.items)
			console.log('payrollTimeSheetTableData', payrollTimeSheetTableData)
			if (!isEqual(payrollTimeSheetData.items, payrollTimeSheetTableData)) {
				// alert('yeah')
				onSubmitButtonClickedHandler("Save");
			}
		}
		// setInputHours(selectedItem.value)
	};

	console.log('payrollTimeSheetTableData out', payrollTimeSheetTableData)
	const filterNonNullData = (data: any, workFormat: any) => {
		return data
			.map((row: any) => {
				const result: any = {};

				// Add top-level fields (like description) if not null
				Object.entries(row).forEach(([key, value]) => {
					if (key === "days") return;
					if (value !== "" && value !== null && value !== undefined) {
						result[key] = value;
					}
				});

				if (row.days) {
					const filteredDays: any = {};

					Object.entries(row.days).forEach(([date, dayData]: any) => {
						const nonNullFields = Object.entries(dayData).reduce((acc: any, [field, val]) => {
							if (val !== null && val !== undefined) {
								acc[field] = val;
							}
							return acc;
						}, {});

						const hasTimeIn = "timeIn" in nonNullFields;
						const hasTimeOut = "timeOut" in nonNullFields;
						const isTimeInOutMode = hasTimeIn || hasTimeOut;

						if (workFormat === 200 && isTimeInOutMode) {
							// ✅ workFormat 100 — require BOTH timeIn and timeOut
							if (hasTimeIn && hasTimeOut) {
								filteredDays[date] = nonNullFields;
							}
							// else: reject — incomplete pair
						} else {
							// Any other workFormat — keep if any non-null field exists
							if (Object.keys(nonNullFields).length > 0) {
								filteredDays[date] = nonNullFields;
							}
						}
					});

					if (Object.keys(filteredDays).length > 0) {
						result.days = filteredDays;
					}
				}

				return result;
			})
			.filter((row: any) => Object.keys(row).length > 0);
	};

	const onSubmitButtonClickedHandler = (selectedItem: any) => {
		console.log(payrollTimeSheetTableData)
		console.log(JSON.stringify(payrollTimeSheetTableData))
		const updatePayloadData =
			payrollTimeSheetTableData &&
			payrollTimeSheetTableData.map((item: any) => ({
				...item,
			}));

		const filtered = filterNonNullData(updatePayloadData, workFormat);
		console.log('filteredfilteredfiltered', JSON.stringify(filtered))

		let payload = {
			clientId: clientId,
			payPeriodEndDate: payPeriodEndDate,
			payrollScheduleId: selectedPayPeriodId,
			employeeId: selectedEmployeeId,
			payPeriodStartDate: payPeriodStarDate,
			workFormat: workFormat,
			inputHours: inputHours,
			timeSheet: filtered
		}

		console.log('updatePayloadData', updatePayloadData)

		if (selectedItem === "Save") {
			submitPayrollTimeSheet(payload)
		} else {
			submitPayrollTimeSheet(payload)
			navigate(`/payroll/${selectedPayPeriodId}`)
			// saveAndCalculatePayrollTimeSheet({ payrollData: updatePayloadData })
		}
	};
	const onDeletePayrollTimeSheetClicked = () => {
		let payload = {
			clientId: clientId,
			payPeriodEndDate: payPeriodEndDate,
			payrollScheduleId: selectedPayPeriodId,
			employeeId: selectedEmployeeId,
			payPeriodStartDate: payPeriodStarDate,
			workFormat: workFormat,
			inputHours: inputHours,
		}
		deletePayrollTimeSheet(payload)
	};

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useEffect(() => {
		if (payrollTimeSheetData) {
			if (payrollTimeSheetData && payrollTimeSheetData.savedData) {
				formMethods.setValue('workFormat', payrollTimeSheetData.savedData)
				setWorkFormat(payrollTimeSheetData.savedData)
			} else {
				formMethods.setValue('workFormat', 100)
				setWorkFormat(100)
			}
		}
	}, [payrollTimeSheetData])

	return (
		<>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}

				sx={{
					mt: "1.7rem"
				}}>
				<BackdropCircularProgress
					open={
						isPending ||
						isPendingPayrollPeriodsDDL ||
						isPendingPayFrequencyDDL ||
						loadingData ||
						isPendingActiveEmployeesByPeriodsDDL ||
						isSubmitPayrollTimeSheet ||
						isDeletePayrollTimeSheet || isPendingClientInformationById ||
						isPendingGeneralSettingsInformationById
					}
					testId="payroll-list"
				/>
				{/* <DeleteConfirmationDialog
					open={openEmployeePayrollDataDialog}
					deleteActionHandler={deleteEmployeePayrollDataField}
					cancelActionHandler={onCloseEmployeePayrollDataDeleteHandler}
					testId="departments-confirmation-dialog"
				/> */}
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>

						{/* Payroll Non-Calculated Data Table */}
						<PayrollTimeSheetHeaderPanel
							payFrequencyDDLItems={payFrequencyDDLItems}
							onFrequencyChangeHandler={onFrequencyChangeHandler}
							payrollPeriodsDDL={payrollPeriodsDDL}
							onPayrollPeriodsDDLChangeHandler={onPayrollPeriodsDDLChangeHandler}
							summary={payrollTimeSheetData && payrollTimeSheetData.summary}
							employeesDDL={activeEmployeesByPeriodsDDL}
							onEmployeeSelectionChangeHandler={onEmployeeSelectionChangeHandler}
							payrollTimeSheetData={payrollTimeSheetData}
							selectedEmployee={selectedEmployeeId}
							onWorkFormatSelectionChangeHandler={onWorkFormatSelectionChangeHandler}
							onInputHoursChangeHandler={onInputHoursChangeHandler}
							savedData={payrollTimeSheetData && payrollTimeSheetData.savedData}
							timeFormat={general_settings && general_settings.timeFormat}
							selectedPayPeriod={payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === formMethods.watch("payrollPeriodsDDL"))}
						/>
						{payrollTimeSheetData && payrollTimeSheetData.items && payrollTimeSheetData.items.length > 0
							&& selectedEmployeeId && workFormat === 100 ?
							<PayrollTimeSheetDataGrid
								columns={payrollTimeSheetData ? payrollTimeSheetData.headerColumns : []}
								rows={
									payrollTimeSheetData && payrollTimeSheetData.items
										? payrollTimeSheetData.items
										: []
								}
								showIndexColumn={true}
								showChecboxColumn={false}
								// showSettingColumn={true}
								// onRowEditClicked={onRowEditClicked}
								// onRowDeleteClicked={onRowDeleteClicked}
								onSnackbarMessageData={(value: any) => setSnackbarData(value)}
								totalRecords={
									payrollTimeSheetData && payrollTimeSheetData.total
										? payrollTimeSheetData.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								// onRequestParamsChange={onRequestParamsChangePayrollData}
								selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
								searchFieldPlaceholder={"Search..."}
								pageTitle={"Payroll"}
								PayrollTimeSheetData={(data: any) =>
									setPayrollTimeSheetTableData(data)
								}
								// tableMargin={"0"}
								tableScrollHeight={true}
								allTotal={payrollTimeSheetData && payrollTimeSheetData.allTotal}
								timeFormat={general_settings && general_settings.timeFormat}
								inputHours={inputHours}
							/> :
							payrollTimeSheetData && payrollTimeSheetData.items && payrollTimeSheetData.items.length > 0
							&& selectedEmployeeId && workFormat === 200 && <PayrollTimeSheetDataGridTimeInOut
								columns={payrollTimeSheetData ? payrollTimeSheetData.headerColumns : []}
								rows={
									payrollTimeSheetData && payrollTimeSheetData.items
										? payrollTimeSheetData.items
										: []
								}
								showIndexColumn={true}
								showChecboxColumn={false}
								// showSettingColumn={true}
								// onRowEditClicked={onRowEditClicked}
								// onRowDeleteClicked={onRowDeleteClicked}
								onSnackbarMessageData={(value: any) => setSnackbarData(value)}
								totalRecords={
									payrollTimeSheetData && payrollTimeSheetData.total
										? payrollTimeSheetData.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								// onRequestParamsChange={onRequestParamsChangePayrollData}
								selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
								searchFieldPlaceholder={"Search..."}
								pageTitle={"Payroll"}
								PayrollTimeSheetData={(data: any) =>
									setPayrollTimeSheetTableData(data)
								}
								// tableMargin={"0"}
								tableScrollHeight={true}
								allTotal={payrollTimeSheetData && payrollTimeSheetData.allTotal}
								timeFormat={general_settings && general_settings.timeFormat}
								inputHours={inputHours}
							/>}

						{payrollTimeSheetData && payrollTimeSheetData.items && payrollTimeSheetData.items.length > 0 && selectedEmployeeId && (
							<PayrollTimeSheetSetupToolbar
								onSubmitButtonClickedHandler={onSubmitButtonClickedHandler}
								onDeleteButtonClicked={onDeletePayrollTimeSheetClicked}
								payrollTimeSheetData={payrollTimeSheetData && payrollTimeSheetData}
								selectedPayPeriod={payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === formMethods.watch("payrollPeriodsDDL"))}
							/>
						)}
					</form>
				</FormProvider>
			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
